<template>
  <div class="university-switcher">
    <el-dropdown
      trigger="click"
      @command="handleCommand"
      placement="bottom-start"
      :max-height="400"
      popper-class="university-dropdown-popper"
      :teleported="true"
    >
      <div class="switcher-trigger">
        <div v-if="authStore.hasSelectedUniversity" class="current-university">
          <el-icon class="university-icon"><School /></el-icon>
          <span class="university-name">{{
            authStore.selectedUniversity?.universityName
          }}</span>
          <el-icon class="arrow-icon"><ArrowDown /></el-icon>
        </div>
        <div v-else class="no-university">
          <el-icon class="university-icon"><School /></el-icon>
          <span class="placeholder-text">{{ $t("universitySwitcher.selectUniversity") }}</span>
          <el-icon class="arrow-icon"><ArrowDown /></el-icon>
        </div>
      </div>

      <template #dropdown>
        <el-dropdown-menu class="university-dropdown-menu">
          <!-- 搜索框 -->
          <div class="dropdown-search" @click.stop>
            <el-input
              v-model="searchQuery"
              :placeholder="$t('universitySwitcher.searchPlaceholder')"
              size="small"
              clearable
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
          </div>

          <!-- 当前选择 -->
          <div v-if="authStore.hasSelectedUniversity" class="current-section">
            <div class="section-title">{{ $t("universitySwitcher.current") }}</div>
            <el-dropdown-item :disabled="true" class="current-item">
              <el-icon class="check-icon"><Check /></el-icon>
              <div class="university-info">
                <div class="name">{{ authStore.selectedUniversity?.universityName }}</div>
                <div class="location">
                  {{ authStore.selectedUniversity?.universityCity }},
                  {{ authStore.selectedUniversity?.universityState }}
                </div>
              </div>
            </el-dropdown-item>
            <el-divider class="section-divider" />
          </div>

          <!-- 大学列表 -->
          <div class="universities-section">
            <div class="section-title">{{ $t("universitySwitcher.switchTo") }}</div>
            <div class="universities-list">
              <el-dropdown-item
                v-for="university in filteredUniversities"
                :key="university.id"
                :command="`select-${university.id}`"
                :class="{ 
                  'is-selected': university.id === authStore.selectedUniversity?.universityId 
                }"
              >
                <div class="university-info">
                  <div class="name">{{ university.name }}</div>
                  <div class="location">{{ university.city }}, {{ university.state }}</div>
                </div>
              </el-dropdown-item>

              <div v-if="filteredUniversities.length === 0" class="no-results">
                {{ $t("universitySwitcher.noResults") }}
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <el-divider class="section-divider" />
          <div class="actions-section">
            <el-dropdown-item command="view-all" class="action-item">
              <el-icon><View /></el-icon>
              {{ $t("universitySwitcher.viewAll") }}
            </el-dropdown-item>
            <el-dropdown-item
              v-if="authStore.hasSelectedUniversity"
              command="clear"
              class="action-item danger"
            >
              <el-icon><Close /></el-icon>
              {{ $t("universitySwitcher.clearSelection") }}
            </el-dropdown-item>
          </div>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { universitiesApi } from "@/api/universities";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  School,
  ArrowDown,
  Search,
  Check,
  View,
  Close,
} from "@element-plus/icons-vue";
import type { University } from "@/types";
import { searchUniversities } from "@/utils/universitySearch";

const router = useRouter();
const authStore = useAuthStore();

const universities = ref<University[]>([]);
const searchQuery = ref("");
const loading = ref(false);

// Computed
const filteredUniversities = computed(() => {
  // 排除当前已选择的大学
  const availableUniversities = universities.value.filter(
    (uni) => uni.id !== authStore.selectedUniversity?.universityId
  );
  
  if (!searchQuery.value.trim()) {
    return availableUniversities;
  }

  // 使用智能搜索（支持别名和首字母联想）
  return searchUniversities(availableUniversities, searchQuery.value);
});

// Methods
const fetchUniversities = async () => {
  loading.value = true;
  try {
    const response = await universitiesApi.getAll();
    universities.value = response.data || [];
  } catch (error) {
    console.error("Failed to fetch universities:", error);
  } finally {
    loading.value = false;
  }
};

const handleCommand = async (command: string) => {
  if (command === "view-all") {
    // 设置 viewAll 标记后跳转
    localStorage.setItem("viewAllUniversities", "true");
    router.push("/courses");
    return;
  }

  if (command === "clear") {
    try {
      await ElMessageBox.confirm(
        "This will show courses from all universities. You can select a university again anytime.",
        "Clear University Selection?",
        {
          confirmButtonText: "Clear",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );

      await authStore.clearUniversity();
      ElMessage.success("University selection cleared");
      // 刷新页面以应用更改
      router.go(0);
    } catch {
      // User cancelled
    }
    return;
  }

  // 切换大学
  if (command.startsWith("select-")) {
    const universityId = parseInt(command.replace("select-", ""));
    try {
      const result = await authStore.selectUniversity(universityId);
      if (result.success) {
        ElMessage.success("University changed successfully!");
        // 刷新页面以应用新的大学过滤
        router.go(0);
      } else {
        ElMessage.error("Failed to change university");
      }
    } catch (error) {
      console.error("Failed to select university:", error);
      ElMessage.error("Failed to change university");
    }
  }
};

// Lifecycle
onMounted(() => {
  fetchUniversities();
});
</script>

<style scoped lang="scss">
.university-switcher {
  display: flex;
  align-items: center;
}

.switcher-trigger {
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px; /* Increased from 160px */
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2);

    &::before {
      left: 100%;
    }

    .arrow-icon {
      transform: rotate(180deg);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
  }
}

.current-university,
.no-university {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.university-icon {
  font-size: 22px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.university-name {
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* No max-width constraint - allow full display */
}

.placeholder-text {
  flex: 1;
  font-size: 14px;
  color: #667eea;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-icon {
  font-size: 16px;
  color: #667eea;
  flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.university-dropdown-menu {
  min-width: 360px;
  max-height: 550px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.2);
  border: 1px solid rgba(102, 126, 234, 0.1);
  overflow: hidden;
  z-index: 2100 !important; /* 确保高于导航栏 */

  :deep(.el-dropdown-menu__item) {
    padding: 14px 18px;
    transition: all 0.3s;
    border-radius: 8px;
    margin: 4px 8px;
  }
}

:deep(.el-popper) {
  z-index: 2100 !important; /* 确保下拉菜单高于所有内容 */
}

/* 全局样式 - 确保下拉菜单popper正确显示 */
</style>

<style>
/* 全局样式 - 不使用scoped */
.university-dropdown-popper {
  z-index: 2100 !important;
  margin-top: 8px !important; /* 添加间距,避免紧贴触发器 */
}

.university-dropdown-popper .el-popper__arrow {
  display: none; /* 可选:隐藏箭头,更现代的设计 */
}

.dropdown-search {
  padding: 16px 16px 12px;
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);

  :deep(.el-input) {
    width: 100%;

    .el-input__wrapper {
      border-radius: 12px;
      border: 2px solid rgba(102, 126, 234, 0.15);
      box-shadow: none;
      transition: all 0.3s;

      &:hover {
        border-color: rgba(102, 126, 234, 0.3);
      }

      &.is-focus {
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }
    }

    .el-input__prefix {
      color: #667eea;
    }
  }
}

.current-section,
.universities-section,
.actions-section {
  padding: 8px 0;
}

.section-title {
  padding: 12px 18px 8px;
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05), transparent);
}

.section-divider {
  margin: 8px 0;
  border-top: 2px solid rgba(102, 126, 234, 0.08);
}

.current-item {
  background: linear-gradient(135deg, #e6f3ff 0%, #f0f5ff 100%) !important;
  border-left: 4px solid #667eea !important;
  cursor: default;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -2px;
    right: -2px;
    width: 60px;
    height: 60px;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.15), transparent);
    border-radius: 50%;
  }

  &:hover {
    background: linear-gradient(135deg, #e6f3ff 0%, #f0f5ff 100%) !important;
  }
}

.check-icon {
  color: #67c23a;
  font-size: 20px;
  margin-right: 10px;
  animation: checkBounce 0.5s ease-out;
}

@keyframes checkBounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

.university-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .name {
    font-size: 15px;
    font-weight: 700;
    color: #303133;
    line-height: 1.4;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .location {
    font-size: 13px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;

    &::before {
      content: "📍";
      font-size: 12px;
    }
  }
}

.universities-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9ff;
    border-radius: 4px;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 4px;
    transition: all 0.3s;

    &:hover {
      background: linear-gradient(135deg, #5568d3, #6a3f8f);
    }
  }

  .el-dropdown-menu__item {
    &.is-selected {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
      border-left: 3px solid #667eea;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.12), rgba(118, 75, 162, 0.12));
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
    }
  }
}

.no-results {
  padding: 32px 16px;
  text-align: center;
  color: #909399;
  font-size: 14px;
  font-weight: 500;

  &::before {
    content: "🔍";
    display: block;
    font-size: 32px;
    margin-bottom: 8px;
    opacity: 0.5;
  }
}

.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;

  .el-icon {
    font-size: 18px;
    transition: transform 0.3s;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15)) !important;
    transform: translateX(4px);

    .el-icon {
      transform: scale(1.2);
    }
  }

  &.danger {
    color: #f56c6c;

    &:hover {
      background: linear-gradient(135deg, rgba(245, 108, 108, 0.1), rgba(245, 108, 108, 0.15)) !important;
    }
  }
}

/* Responsive styles */
@media (max-width: 768px) {
  .switcher-trigger {
    min-width: 160px;
    padding: 8px 12px;
  }

  .university-name,
  .placeholder-text {
    font-size: 14px;
  }

  .university-dropdown-menu {
    min-width: 320px;
  }
}

@media (max-width: 480px) {
  .switcher-trigger {
    min-width: auto;
    padding: 8px 12px;
  }

  .university-name,
  .placeholder-text {
    display: none;
  }

  .university-dropdown-menu {
    min-width: 300px;
  }
}
</style>
